import { Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { ICourse } from '../api/CourseApi';
import { ILesson, LessonApi } from '../api/LessonApi';
import { UserDialogPromptApi } from '../api/UserDialogPromptApi';
import AppBarContext from '../context/AppBarContext';
import { isReviewCourse } from '../page/Lesson';
import { handleErrorMessage } from './ErrorDialog';
import { isReviewLessonHidden } from './ReviewView';
// npm install react-markdown@7.1.2 --legacy-peer-deps

interface CourseViewProps {
    course: ICourse;
}

const CourseView: React.FC<CourseViewProps> = ({ course }) => {
    const [description, setDescription] = React.useState(course.description);
    const [lessonList, setLessonList] = React.useState<ILesson[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);

    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();

    React.useEffect(() => {
        setDescription(course.description || '');
        setIsLoading(true);
        setShowSkeleton(false);

        // Delay showing skeleton by 200ms
        const delay = setTimeout(() => {
            setShowSkeleton(true);
        }, 300);

        // Fetch lessons when course changes
        LessonApi.getAll(course.id)
            .then(response => {
                const lessonList = response.data;
                if (isReviewCourse(course)) {


                    UserDialogPromptApi.getUserDialogPromptReview()
                        .then(response => {
                            if (response.status < 300) {
                                return response.data;
                            }
                            throw new Error("UserDialogPromptApi.getUserDialogPromptReview() " + response.status)

                        })
                        .then(userDialogPromptReviewResponse => {
                            setLessonList(lessonList
                                .map((lesson) => {
                                    const is_hidden = isReviewLessonHidden(userDialogPromptReviewResponse, lesson, course);
                                    return {
                                        ...lesson,
                                        is_hidden: is_hidden
                                    };
                                })
                                .filter((lesson) => lesson.is_hidden === false)
                                .sort((a, b) => a.order_index - b.order_index)
                            )
                        })
                        .catch(err => {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        })


                } else {
                    setLessonList(lessonList.filter((lesson) => lesson.is_hidden === false).sort((a, b) => a.order_index - b.order_index));
                }

            })
            .catch(err => {
                console.error("Failed to load lessons:", err);
            })
            .finally(() => {
                setIsLoading(false);
            });

        return () => clearTimeout(delay); // Cleanup
    }, [course, history, setErrorMessage]);

    const handleLessonClick = (lesson: ILesson) => {
        // Navigate to /lesson/:courseId/:lessonId
        history.push(`/lesson/${course.id}/${lesson.id}`);
    };

    return (
        <Stack
            spacing={4}
            sx={{
                width: '100%',
                pt: 2,
                pl: 4,
                whiteSpace: 'pre-line',
                alignItems: 'flex-start' // 👈 aligns children (like Typography) to the left
            }}
        >
            {description && (
                <Typography sx={{ textAlign: 'left', width: '100%' }}>
                    <ReactMarkdown>{description}</ReactMarkdown>
                </Typography>
            )}

            <Stack sx={{ width: '100%', mt: 4 }}>
                <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
                    {(isLoading && showSkeleton)
                        ? [...Array(6)].map((_, index) => (
                            <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, m: 1 }} />
                            </Grid>
                        ))
                        : lessonList.map(lesson => (
                            <Grid item key={lesson.id} xs={12} sm={6} md={4} lg={3}>
                                <Button
                                    fullWidth
                                    onClick={() => handleLessonClick(lesson)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        p: 2,
                                        m: 1,
                                        transition: 'border 0.2s ease, color 0.2s ease',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: '#FCCD56',
                                        }
                                    }}
                                >
                                    {lesson.name}
                                </Button>
                            </Grid>
                        ))
                    }
                </Grid>
            </Stack>
        </Stack>
    );
};

export default CourseView;